import { LegalPage } from "../components/LegalPage";

const LAST_UPDATED = "2026-10-22";

const SECTIONS = [
  {
    title: "Who We Are",
    body: ({ email }) => (
      <>
        <p>
          Elin's Consultancy is the data controller for the personal data described in
          this policy. This policy explains what personal data we collect from visitors to
          our website and clients of our consultancy, why we collect it, how we store and
          protect it, how long we keep it, and your rights under UK GDPR and the Data
          Protection Act 2018.
        </p>
        <p>
          If you have any questions about this policy or wish to exercise any of your
          rights, please email us at{" "}
          <a href={`mailto:${email}`}>{email}</a>.
        </p>
      </>
    ),
  },
  {
    title: "What Personal Data We Collect",
    body: () => (
      <>
        <p>We only collect personal data that you choose to share with us. The main sources are:</p>
        <ul>
          <li>
            <strong>Enquiry form on the Contact page.</strong> Name, email address, phone
            number (optional), the type of enquiry you select, and the message you write.
          </li>
          <li>
            <strong>WhatsApp lead-capture form.</strong> Name, phone number and a short
            message. When you choose "Open WhatsApp" we pass this through so your
            messaging client opens with the message pre‑filled.
          </li>
          <li>
            <strong>Email or phone correspondence.</strong> Anything you share with us
            when you reply to our emails or call the business line.
          </li>
          <li>
            <strong>Client file.</strong> Where you engage us for a consultancy
            instruction, we collect the details necessary to deliver the work — for
            example billing address, VAT details for business clients, and property
            details.
          </li>
          <li>
            <strong>Technical data.</strong> Standard web‑server access logs
            (IP address, user agent, timestamps) retained briefly for security.
          </li>
        </ul>
        <p>
          Our tools (the Stamp Duty Calculator, HMO Licensing Checker, Rentable Yield
          Snapshot) run entirely in your browser and do not transmit the values you
          enter to our servers.
        </p>
      </>
    ),
  },
  {
    title: "Why We Collect It (Legal Basis)",
    body: () => (
      <>
        <p>
          Under UK GDPR we rely on the following legal bases:
        </p>
        <ul>
          <li>
            <strong>Consent (Article 6(1)(a))</strong> — when you fill in a contact or
            WhatsApp form, so that we can respond to your enquiry.
          </li>
          <li>
            <strong>Contract (Article 6(1)(b))</strong> — to deliver the services you
            instruct us to carry out and to invoice you for them.
          </li>
          <li>
            <strong>Legal obligation (Article 6(1)(c))</strong> — for example, retaining
            invoices and client records for tax and accounting purposes.
          </li>
          <li>
            <strong>Legitimate interests (Article 6(1)(f))</strong> — to keep our systems
            secure, prevent fraud and manage our business. We assess that these interests
            do not override your rights and freedoms.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "How We Store and Protect It",
    body: () => (
      <>
        <p>
          Personal data is stored in access‑controlled systems operated by reputable
          providers inside the UK or the European Economic Area. Account access is
          restricted to Elin's Consultancy staff who need it for their work and is
          protected by strong passwords and, where supported, two‑factor authentication.
        </p>
        <p>
          Transfers to any sub‑processors outside the UK/EEA (for example our transactional
          email provider) take place under contracts incorporating UK‑approved safeguards
          such as the International Data Transfer Addendum.
        </p>
        <p>
          Despite our best efforts, no system is 100% secure. If a personal data breach is
          likely to result in a risk to your rights and freedoms, we will notify the ICO
          within 72 hours and tell you without undue delay where required.
        </p>
      </>
    ),
  },
  {
    title: "Who We Share It With",
    body: () => (
      <>
        <p>We only share personal data where there is a clear reason:</p>
        <ul>
          <li>
            <strong>Service providers</strong> — our hosting, email, document and payment
            platforms. They act as processors on our instructions and are bound by written
            agreements.
          </li>
          <li>
            <strong>Introduced professionals</strong> — only with your prior agreement, we
            may share your contact details with a solicitor, broker, surveyor or developer
            you ask us to connect you with.
          </li>
          <li>
            <strong>Regulators and law enforcement</strong> — where we are legally
            required to do so.
          </li>
        </ul>
        <p>We do not sell your personal data to anyone and we do not run any form of ad targeting.</p>
      </>
    ),
  },
  {
    title: "How Long We Keep It",
    body: () => (
      <>
        <p>We keep personal data no longer than necessary:</p>
        <ul>
          <li>
            <strong>Enquiries that do not become clients:</strong> up to 24 months, then
            deleted.
          </li>
          <li>
            <strong>Client files and invoices:</strong> at least 6 years after the end of
            the engagement, to comply with HMRC and limitation‑period requirements.
          </li>
          <li>
            <strong>WhatsApp leads:</strong> up to 24 months unless they become part of a
            client file.
          </li>
          <li>
            <strong>Website logs:</strong> up to 90 days for operational security.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Your Rights Under UK GDPR",
    body: ({ email }) => (
      <>
        <p>You have the right to:</p>
        <ul>
          <li>Be informed about how we use your data (this policy).</li>
          <li>Request a copy of the personal data we hold about you (right of access).</li>
          <li>Have inaccurate data corrected (rectification).</li>
          <li>Ask us to erase your data where there is no good reason to retain it (erasure).</li>
          <li>Restrict our processing of your data in certain circumstances.</li>
          <li>Object to processing based on legitimate interests or direct marketing.</li>
          <li>Request data portability for data you provided to us by consent or contract.</li>
          <li>Withdraw consent at any time, where consent was the legal basis.</li>
        </ul>
        <p>
          To exercise any of these rights, email{" "}
          <a href={`mailto:${email}`}>{email}</a>. We will respond within one month. If
          you are not satisfied with our response, you have the right to complain to the
          Information Commissioner's Office at{" "}
          <a href="https://ico.org.uk/" target="_blank" rel="noreferrer">ico.org.uk</a>.
        </p>
      </>
    ),
  },
  {
    title: "Cookies and Analytics",
    body: () => (
      <>
        <p>
          Our website uses only the strictly‑necessary cookies required to keep you signed
          into the admin back office and to run the site. We do not use third‑party
          advertising cookies.
        </p>
        <p>
          If we add analytics or marketing cookies in the future we will update this
          policy and ask for your consent before setting them.
        </p>
      </>
    ),
  },
  {
    title: "Children",
    body: () => (
      <>
        <p>
          Our services are aimed at adults and we do not knowingly collect personal data
          from children under 13. If you believe we have inadvertently done so, please
          contact us and we will delete it.
        </p>
      </>
    ),
  },
  {
    title: "Changes to this Policy",
    body: () => (
      <>
        <p>
          We may update this policy from time to time. The "Last updated" date at the top
          shows when the current version came into effect. Material changes will be
          highlighted on the website.
        </p>
      </>
    ),
  },
];

const PrivacyPolicy = () => (
  <LegalPage
    metaTitle="Privacy Policy | Elin's Consultancy"
    metaDescription="How Elin's Consultancy collects, uses and protects personal data under UK GDPR, and the rights visitors have to their data."
    eyebrow="Legal"
    titleLines={[
      "Privacy",
      <>
        <em className="italic font-normal text-gold-soft">Policy</em>
      </>,
    ]}
    intro="How we handle your personal data under UK GDPR — the plain-English version."
    lastUpdated={LAST_UPDATED}
    sections={SECTIONS}
  />
);

export default PrivacyPolicy;
